import api from './api';
import type {
  ApiResponse,
  AdminStats,
  AdminUser,
  PrelaunchSubscription,
  SendInvitationsResult,
  UserRole,
  PublicConfig,
} from '../types';

// ============================================
// Configuration publique (sans authentification)
// ============================================

/**
 * Récupère la configuration publique
 */
export async function getPublicConfig(): Promise<ApiResponse<PublicConfig>> {
  const response = await api.get('/config/public');
  return response.data;
}

/**
 * Valide un token d'invitation pré-lancement
 */
export async function validateInvitationToken(
  token: string
): Promise<ApiResponse<{ email: string }>> {
  const response = await api.get(`/config/validate-invitation/${token}`);
  return response.data;
}

// ============================================
// Statistiques et configuration admin
// ============================================

/**
 * Récupère les statistiques admin
 */
export async function getAdminStats(): Promise<ApiResponse<AdminStats>> {
  const response = await api.get('/admin/stats');
  return response.data;
}

/**
 * Récupère la configuration complète
 */
export async function getAdminConfig(): Promise<ApiResponse<Record<string, string>>> {
  const response = await api.get('/admin/config');
  return response.data;
}

/**
 * Met à jour une valeur de configuration
 */
export async function updateConfig(
  key: string,
  value: string
): Promise<ApiResponse<void>> {
  const response = await api.put('/admin/config', { key, value });
  return response.data;
}

// ============================================
// Gestion des utilisateurs
// ============================================

/**
 * Récupère la liste des utilisateurs
 */
export async function getUsers(filters?: {
  role?: UserRole;
  blocked?: boolean;
  search?: string;
}): Promise<ApiResponse<AdminUser[]>> {
  const params = new URLSearchParams();

  if (filters?.role) {
    params.append('role', filters.role);
  }
  if (filters?.blocked !== undefined) {
    params.append('blocked', String(filters.blocked));
  }
  if (filters?.search) {
    params.append('search', filters.search);
  }

  const response = await api.get(`/admin/users?${params.toString()}`);
  return response.data;
}

/**
 * Met à jour le rôle d'un utilisateur
 */
export async function updateUserRole(
  userId: string,
  role: UserRole
): Promise<ApiResponse<void>> {
  const response = await api.put(`/admin/users/${userId}/role`, { role });
  return response.data;
}

/**
 * Bloque ou débloque un utilisateur
 */
export async function blockUser(
  userId: string,
  blocked: boolean
): Promise<ApiResponse<void>> {
  const response = await api.put(`/admin/users/${userId}/block`, { blocked });
  return response.data;
}

/**
 * Force un utilisateur à changer son mot de passe
 */
export async function forcePasswordChange(
  userId: string,
  force: boolean
): Promise<ApiResponse<void>> {
  const response = await api.put(`/admin/users/${userId}/force-password-change`, { force });
  return response.data;
}

/**
 * Supprime un utilisateur
 */
export async function deleteUser(userId: string): Promise<ApiResponse<void>> {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
}

// ============================================
// Gestion des pré-inscriptions
// ============================================

/**
 * Récupère la liste des pré-inscriptions
 */
export async function getPrelaunchSubscriptions(filters?: {
  status?: string;
  search?: string;
}): Promise<ApiResponse<PrelaunchSubscription[]>> {
  const params = new URLSearchParams();

  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.search) {
    params.append('search', filters.search);
  }

  const response = await api.get(`/admin/prelaunch?${params.toString()}`);
  return response.data;
}

/**
 * Envoie des invitations à des pré-inscrits sélectionnés
 */
export async function sendInvitations(
  subscriptionIds: string[]
): Promise<ApiResponse<SendInvitationsResult>> {
  const response = await api.post('/admin/prelaunch/send-invitations', {
    subscriptionIds,
  });
  return response.data;
}

/**
 * Envoie des invitations à tous les pending
 */
export async function sendAllPendingInvitations(): Promise<
  ApiResponse<SendInvitationsResult>
> {
  const response = await api.post('/admin/prelaunch/send-all-pending');
  return response.data;
}

/**
 * Rejette une pré-inscription
 */
export async function rejectSubscription(
  subscriptionId: string
): Promise<ApiResponse<void>> {
  const response = await api.put(`/admin/prelaunch/${subscriptionId}/reject`);
  return response.data;
}

/**
 * Supprime une pré-inscription
 */
export async function deleteSubscription(
  subscriptionId: string
): Promise<ApiResponse<void>> {
  const response = await api.delete(`/admin/prelaunch/${subscriptionId}`);
  return response.data;
}

/**
 * Envoie un email de relance à un pré-inscrit
 */
export async function sendFollowUp(
  subscriptionId: string
): Promise<ApiResponse<void>> {
  const response = await api.post(`/admin/prelaunch/${subscriptionId}/follow-up`);
  return response.data;
}

// ============================================
// Gestion du Rate Limiting
// ============================================

export interface RateLimitConfigItem {
  windowMs: number;
  max: number;
  windowFormatted: string;
  description: string;
}

export interface RateLimitConfig {
  read: RateLimitConfigItem;
  write: RateLimitConfigItem;
  auth: RateLimitConfigItem;
  prelaunch: RateLimitConfigItem;
  export: RateLimitConfigItem;
}

export interface ResetRateLimitResult {
  key: string;
  resetLimiters: string[];
}

/**
 * Récupère la configuration du rate limiting
 */
export async function getRateLimitConfig(): Promise<ApiResponse<RateLimitConfig>> {
  const response = await api.get('/admin/rate-limit/config');
  return response.data;
}

/**
 * Réinitialise le rate limit pour un utilisateur
 */
export async function resetUserRateLimit(
  userId: string,
  limiterType?: string
): Promise<ApiResponse<ResetRateLimitResult>> {
  const response = await api.post('/admin/rate-limit/reset', {
    userId,
    limiterType: limiterType || 'all',
  });
  return response.data;
}

/**
 * Réinitialise le rate limit pour une IP
 */
export async function resetIpRateLimit(
  ip: string,
  limiterType?: string
): Promise<ApiResponse<ResetRateLimitResult>> {
  const response = await api.post('/admin/rate-limit/reset', {
    ip,
    limiterType: limiterType || 'all',
  });
  return response.data;
}

// ============================================
// Quotas et clés actives (Redis)
// ============================================

export interface RateLimitStatus {
  key: string;
  type: 'user' | 'ip';
  identifier: string;
  limiter: string;
  count: number;
  max: number;
  remaining: number;
  resetAt: string | null;
  ttl: number;
}

/**
 * Liste toutes les clés rate limit actives
 */
export async function getActiveRateLimitKeys(): Promise<ApiResponse<RateLimitStatus[]>> {
  const response = await api.get('/admin/rate-limit/active');
  return response.data;
}

/**
 * Liste les clés bloquées (ayant atteint la limite)
 */
export async function getBlockedKeys(): Promise<ApiResponse<RateLimitStatus[]>> {
  const response = await api.get('/admin/rate-limit/blocked');
  return response.data;
}

/**
 * Récupère le statut rate limit d'un utilisateur spécifique
 */
export async function getUserRateLimitStatus(
  userId: string
): Promise<ApiResponse<RateLimitStatus[]>> {
  const response = await api.get(`/admin/rate-limit/user/${userId}`);
  return response.data;
}
